import { useState } from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../lib/api";
import { NOTICE_TONES, NoticeBox } from "./NoticeBox";

export const EMPTY_NOTICE = { tone: "red", title: "", text: "", visible_to_client: false };

/* Tone toggle + heading + text. `value` = {tone, title, text, visible_to_client}; `clientToggle` shows the portal switch (jobs/requests, not quotes).
   `aiContext` = {entity, title, description} lets "AI wording" turn a rough note into a clear red/amber/green notice in one click. */
export function NoticeEditor({ value, onChange, clientToggle = false, testid = "notice", compact = false, aiContext = null }) {
  const v = { ...EMPTY_NOTICE, ...(value || {}) };
  const tone = NOTICE_TONES[v.tone] || NOTICE_TONES.red;
  const set = (patch) => onChange({ ...v, ...patch });
  const [aiBusy, setAiBusy] = useState(false);

  const aiWord = async () => {
    if (v.text.trim().length < 5) { toast.error("Type a rough note in the box first — the AI will word it properly"); return; }
    setAiBusy(true);
    const before = { tone: v.tone, title: v.title, text: v.text };
    try {
      const { data } = await API.post("/ai/notice", { rough: v.text, tone: v.tone, context: aiContext || {} });
      onChange({ ...v, tone: data.tone || v.tone, title: data.title || "", text: data.text });
      const t = NOTICE_TONES[data.tone] || tone;
      toast.success(`Notice worded — ${t.label}`, { action: { label: "Undo", onClick: () => onChange({ ...v, ...before }) }, duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "The AI couldn't word that just now");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testid}-editor`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-xs">Notice <span className="text-[10px] text-muted-foreground font-normal">(optional — only shown when it has text)</span></Label>
        <div className="flex items-center gap-2">
          <div className="flex gap-1" role="radiogroup" aria-label="Notice tone">
            {Object.entries(NOTICE_TONES).map(([key, t]) => (
              <button key={key} type="button" role="radio" aria-checked={v.tone === key} onClick={() => set({ tone: key })}
                      className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${v.tone === key ? t.pill : t.pillOff}`} data-testid={`${testid}-tone-${key}`}>
                {t.label}
              </button>
            ))}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={aiWord} disabled={aiBusy || !v.text.trim()} title="Turn your rough note into a clear notice — picks the tone, heading and wording"
                  className="h-7 text-[11px] border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40" data-testid={`${testid}-ai-btn`}>
            {aiBusy ? <ArrowClockwise size={12} className="mr-1 animate-spin" /> : <Sparkle size={12} weight="fill" className="mr-1" />} {aiBusy ? "Wording…" : "AI wording"}
          </Button>
        </div>
      </div>
      <Input value={v.title} onChange={(e) => set({ title: e.target.value })} placeholder={`Heading — defaults to "${tone.defaultTitle}"`} className="h-8 text-xs" data-testid={`${testid}-title`} />
      <Textarea value={v.text} onChange={(e) => set({ text: e.target.value })} rows={compact ? 2 : 3} className="text-xs"
                placeholder={v.tone === "red" ? "e.g. Asbestos present in the boiler room — do not disturb lagging. Isolate mains before work." : v.tone === "amber" ? "e.g. Parking is restricted before 9:30am — use the rear service entrance." : "e.g. Client has pre-approved out-of-hours access — no waiting on site."}
                data-testid={`${testid}-text`} />
      <p className="text-[10px] text-muted-foreground">Tip: jot the note roughly, then click <span className="font-semibold">AI wording</span> — it picks red / amber / green, writes the heading and tidies the text.</p>
      {clientToggle && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
          <p className="text-[11px] text-muted-foreground">Also show this notice in the client portal</p>
          <Switch checked={!!v.visible_to_client} onCheckedChange={(c) => set({ visible_to_client: c })} data-testid={`${testid}-client-toggle`} />
        </div>
      )}
      {v.text.trim() && <NoticeBox notice={v} compact testid={`${testid}-preview`} />}
    </div>
  );
}
